// What each kind of board draws in the browser. A canvas is the board as it has always been (Canvas,
// Rail, ZoomControls in App.tsx); every other kind brings its own surface, and its own island where the
// rail sits, inside the same scene, board pill, chat, Connect and Data panel (Shell.tsx). The views load
// only when a board of their kind opens.

import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { Calendar, Compass, FileText, FolderOpen, LayoutDashboard, Network, CalendarDays, Presentation, SquareKanban, Table2, WandSparkles, type LucideIcon } from "lucide-react";
import { BOARD_KINDS, KIND_ORDER } from "../../shared/boardKinds";
import { partsOf } from "../../shared/plan";
import { recordCount } from "../../shared/records";
import type { Board, BoardKind, Entry, Op, WidgetItem } from "../../shared/types";
import { DocThumb } from "./doc/Thumb";
import { placeInPage } from "./doc/place";
// the Calendar's columns, header, rail, quick add and card, kept from the planner it replaced, before its own styles
import "../styles/agenda-parts.css";
import { MapThumb } from "./mindmap/Thumb";
import { placeBesideMap } from "./mindmap/place";
import { CalendarThumb } from "./calendar/Thumb";
import { placeOnShelf } from "./calendar/place";
import { PipelineThumb } from "./pipeline/Thumb";
import { FilesThumb } from "./files/Thumb";
import { CustomThumb } from "./custom/Thumb";
import { placeOnShelf as placeOnPipelineShelf } from "./pipeline/place";
import { SheetThumb, sheetCount } from "./sheet/ThumbLazy";
import { SlidesThumb, slidesCount } from "./slides/Thumb";
import { placeOnSlide } from "./slides/place";
import { AgendaThumb, agendaCount } from "./agenda/Thumb";
import "../styles/agenda.css";

/**
 * Where a widget someone adds goes on a typed board: the ops that put it there, or why it cannot go yet.
 * onCanvas: it went on the board's canvas, so it is selected and shown as on a canvas.
 */
export type Placement = { ops: Op[]; onCanvas?: boolean } | { refused: { title: string; body?: string } };

export interface KindView {
  /** the board's surface in the canvas's place, with its own island where the rail sits */
  View: LazyExoticComponent<ComponentType<{ board: Board }>>;
  /** on a phone, in the column's place; readOnly on a shared link */
  Phone: LazyExoticComponent<ComponentType<{ board: Board; readOnly?: boolean }>>;
  /** a shared link's read-only view on a wide screen; a kind without one cannot be shared yet */
  Shared?: LazyExoticComponent<ComponentType<{ board: Board }>>;
  /** the board in small, in its tile in All boards, in the board's own tile colors */
  Thumb?: ComponentType<{ board: Board; entries: Entry[] }>;
  /** where a widget the user adds from the gallery (or anywhere else) goes on it */
  place?: (item: WidgetItem, board: Board) => Placement;
  /** what it holds, counted, for its tile in All boards ("12 pages") */
  count?: (entries: Entry[]) => string;
  /** on a phone, its widgets in a column under its own view, as a canvas shows them (they float beside it) */
  phoneWidgets?: boolean;
}

export const KINDS: Partial<Record<BoardKind, KindView>> = {
  doc: {
    View: lazy(() => import("./doc/DocView")),
    Phone: lazy(() => import("./doc/DocPhone")),
    Shared: lazy(() => import("./doc/DocShared")),
    Thumb: DocThumb,
    // widgets go inside the open page, after the cursor's block
    place: (item, board) => placeInPage(item, board),
  },
  // a plan: a doc board's pages underneath, drawn as a room the user shares with their agent (plan/PlanView.tsx)
  plan: {
    View: lazy(() => import("./plan/PlanView")),
    Phone: lazy(() => import("./plan/PlanPhone")),
    Shared: lazy(() => import("./plan/PlanShared")),
    Thumb: DocThumb,
    place: (item, board) => placeInPage(item, board),
    count: (entries) => {
      const n = partsOf(entries.filter((e) => e.t === "page")).length;
      return n === 1 ? "1 step" : `${n} steps`;
    },
  },
  mindmap: {
    View: lazy(() => import("./mindmap/MapView")),
    Phone: lazy(() => import("./mindmap/MapPhone")),
    Shared: lazy(() => import("./mindmap/MapShared")),
    Thumb: MapThumb,
    // widgets float beside the map, on its canvas
    place: (item) => placeBesideMap(item),
    phoneWidgets: true,
    count: (entries) => {
      const n = entries.filter((e) => e.t === "node").length;
      return n === 1 ? "1 idea" : `${n} ideas`;
    },
  },
  calendar: {
    View: lazy(() => import("./calendar/CalendarView")),
    Phone: lazy(() => import("./calendar/CalendarPhone")),
    Shared: lazy(() => import("./calendar/CalendarShared")),
    Thumb: CalendarThumb,
    // widgets go last on the shelf down the right
    place: (item, board) => placeOnShelf(item, board),
  },
  // the project folders on the user's computer, read as notes: kept nowhere, so never shared and holding no widgets
  files: {
    View: lazy(() => import("./files/FilesView")),
    Phone: lazy(() => import("./files/FilesPhone")),
    Thumb: FilesThumb,
    count: () => "your project folders",
    place: () => ({ refused: { title: "Widgets go on another board", body: "A Files board shows the folders on your computer and holds no widgets. Put this one on a canvas or a doc board." } }),
  },
  // a sheet: tabs of cells and formulas, its widgets in a shelf down the right as a pipeline's are
  sheet: {
    View: lazy(() => import("./sheet/SheetView")),
    Phone: lazy(() => import("./sheet/SheetPhone")),
    Shared: lazy(() => import("./sheet/SheetShared")),
    Thumb: SheetThumb,
    place: (item, board) => placeOnPipelineShelf(item, board),
    count: sheetCount,
  },
  // a deck: its slides down the left, the open one in the middle at 16:9; a widget goes on a slide of its own
  slides: {
    View: lazy(() => import("./slides/SlidesView")),
    Phone: lazy(() => import("./slides/SlidesPhone")),
    Shared: lazy(() => import("./slides/SlidesShared")),
    Thumb: SlidesThumb,
    place: (item, board) => placeOnSlide(item, board),
    count: slidesCount,
  },
  // the user's own events by day, week and month (kind agenda); no widgets, and not shared until it shows only Busy
  agenda: {
    View: lazy(() => import("./agenda/AgendaView")),
    Phone: lazy(() => import("./agenda/AgendaPhone")),
    Thumb: AgendaThumb,
    count: agendaCount,
    place: () => ({ refused: { title: "Widgets go on another board", body: "A Calendar board shows your events and holds no widgets. Put this one on a canvas or a doc board." } }),
  },
  // the app the user's agent wrote, filling the board, and its records; never shared (yours only)
  custom: {
    View: lazy(() => import("./custom/CustomView")),
    Phone: lazy(() => import("./custom/CustomPhone")),
    Thumb: CustomThumb,
    count: (entries) => recordCount(entries.filter((e) => e.t === "record").length),
    place: () => ({ refused: { title: "A Custom board is one app", body: "Ask your agent to change the app instead. Put this widget on a canvas or a doc board." } }),
  },
  // a client pipeline: deals in stage columns, a widget shelf down the right; never shared (it holds other people's details)
  pipeline: {
    View: lazy(() => import("./pipeline/PipelineView")),
    Phone: lazy(() => import("./pipeline/PipelinePhone")),
    Thumb: PipelineThumb,
    place: (item, board) => placeOnPipelineShelf(item, board),
  },
};

export const KIND_ICON: Record<BoardKind, LucideIcon> = {
  canvas: LayoutDashboard,
  doc: FileText,
  mindmap: Network,
  calendar: CalendarDays,
  pipeline: SquareKanban,
  plan: Compass,
  files: FolderOpen,
  sheet: Table2,
  slides: Presentation,
  agenda: Calendar,
  custom: WandSparkles,
};

/** Whether this build can draw boards of a kind: a canvas always can. */
export function hasView(kind: BoardKind): boolean {
  return kind === "canvas" || !!KINDS[kind];
}

/**
 * The kinds the new-board picker offers: the finished ones, and on a dev server with ?kinds=all in the address, every
 * kind this build can draw, so an unfinished one can be tried before it ships.
 */
export function pickerKinds(search = typeof location !== "undefined" ? location.search : ""): BoardKind[] {
  const all = import.meta.env.DEV && new URLSearchParams(search).get("kinds") === "all";
  return KIND_ORDER.filter((k) => BOARD_KINDS[k].ready || (all && hasView(k)));
}

/** Whether a board of this kind can be shared by link: it has a read-only view to show. */
export function canShare(kind: BoardKind): boolean {
  return kind === "canvas" || (BOARD_KINDS[kind].shareable && !!KINDS[kind]?.Shared);
}
